import { EntityRoutes } from 'routes/constants';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { formatDuration, hasFeatureFlag } from 'UI/utils';

const SUPPORT_ACTIVITY_COLUMNS = hasFeatureFlag(
  FeatureFlags.OverallPerformanceFeedbackActivityColumns
)
  ? [
      {
        name: 'lastFeedbackActivityType',
        label: 'Support Activity Type',
        options: {
          sort: true,
          display: true
        }
      },
      {
        name: 'lastFeedbackActivityDate',
        label: 'Support Activity Date',
        options: {
          sort: true,
          display: true,
          renderer: 'date'
        }
      },
      {
        name: 'lastFeedbackActivityGiverName',
        label: 'Support Activity By',
        options: {
          sort: true,
          display: true
        }
      }
    ]
  : [];

export const COLUMNS = [
  {
    name: 'idFortpac',
    options: {
      filter: false,
      display: 'excluded'
    }
  },
  {
    name: 'empNo',
    options: {
      filter: false,
      display: 'excluded'
    }
  },
  {
    name: 'recruiterName',
    label: 'Name',
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'linkBuilder',
        config: {
          route: EntityRoutes.CommandCenterOverviewRecruiter,
          idKey: 'empNo',
          textKey: 'name',
          enableInTabRedirect: true
        }
      },
      setCellHeaderProps: () => ({ className: 'dataTableStickyLeftColumnHeader' }),
      setCellProps: () => ({
        className: 'dataTableStickyLeftColumn'
      })
    }
  },
  {
    name: 'team',
    label: 'Team',
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'linkBuilder',
        config: {
          route: EntityRoutes.CommandCenterOverviewTeam,
          idKey: 'managerEmpNo',
          textKey: 'team',
          enableInTabRedirect: true
        }
      }
    }
  },
  {
    name: 'healthStatus',
    label: 'Current Health Status',
    options: {
      sort: true,
      display: true,
      renderer: 'healthStatusChip'
    }
  },
  {
    name: 'hireDate',
    label: 'Start Date',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'directorName',
    label: 'Director',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'teamSupervisorsNames',
    label: 'Support Team',
    options: {
      sort: false,
      display: true,
      setCellHeaderProps: () => ({
        style: { minWidth: 200 }
      })
    }
  },
  {
    name: 'tenure',
    label: 'Tenure',
    options: {
      sort: true,
      display: true,
      renderer: 'tenure'
    }
  },
  {
    name: 'daysInStatus',
    label: 'Days in Status *',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'inMails',
    label: 'InMails',
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'goalsCount',
        config: {
          key: 'inMails',
          goalKey: 'inMailsGoals'
        }
      }
    }
  },
  {
    name: 'avgInMails',
    label: 'Avg Daily InMails',
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'goalsCount',
        config: {
          key: 'avgInMails',
          goalKey: 'avgInMailsGoals'
        }
      }
    }
  },
  {
    name: 'bulks',
    label: 'Bulks',
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'goalsCount',
        config: {
          key: 'bulks',
          goalKey: 'bulksGoal'
        }
      }
    }
  },
  {
    name: 'peopleReached',
    label: 'People Reached',
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'goalsCount',
        config: {
          key: 'peopleReached',
          goalKey: 'peopleReachedGoal'
        }
      }
    }
  },
  {
    name: 'avgTodayLivePosts',
    label: 'Avg Daily Active Posts',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'newLivePosts',
    label: 'New Posts',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'calls',
    label: 'Calls',
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'goalsCount',
        config: {
          key: 'calls',
          goalKey: 'callsGoal'
        }
      }
    }
  },
  {
    name: 'callsTime',
    label: 'Phone Time',
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'goalsCount',
        config: {
          key: 'callsTime',
          goalKey: 'callsTimeGoal',
          formatter: formatDuration
        }
      }
    }
  },
  {
    name: 'sendouts',
    label: 'Sendouts',
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'goalsCount',
        config: {
          key: 'sendouts',
          goalKey: 'sendoutsGoal'
        }
      }
    }
  },
  {
    name: 'sendovers',
    label: 'Sendovers',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'placements',
    label: 'Placements',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'feeAgreementsSent',
    label: 'Fee Agreements Sent',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'feeAgreementsSigned',
    label: 'Signed Agreements',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'notSignedCompaniesJo',
    label: 'Not Signed Comp. Job Orders',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'signedCompaniesJo',
    label: 'Signed Comp. Job Orders',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'candidatesAdded',
    label: 'Candidates',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'contactsAdded',
    label: 'Contacts',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'haAdded',
    label: 'Hiring Authorities',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'companiesAdded',
    label: 'Companies',
    options: {
      sort: true,
      display: true,
      renderer: 'formattedNumber'
    }
  },
  {
    name: 'estimates',
    label: 'Estimates',
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'avgFeePercentage',
    label: 'Avg Fee %',
    options: {
      sort: true,
      display: true,
      renderer: 'percentage'
    }
  },
  {
    name: 'avgFeeMoney',
    label: 'Avg Fee',
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'billings',
    label: 'Billings',
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'production',
    label: 'Production',
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'cashIn',
    label: 'Cash in',
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'outStandingBillings',
    label: 'Current O. Billings',
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'drawBalance',
    label: 'Current Draw Balance',
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'productionYTD',
    label: 'Production YTD',
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'cashInYTD',
    label: 'Cash in YTD',
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'careerCashIn',
    label: 'Career Cash in',
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  {
    name: 'trackingProduction',
    label: 'Tracking Production',
    options: {
      sort: true,
      display: true,
      renderer: 'currency_fixed'
    }
  },
  ...SUPPORT_ACTIVITY_COLUMNS,
  {
    name: 'recruitedBy',
    label: 'Recruited By',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'channelPartner1Name',
    label: 'Channel P. 1',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'channelPartner2Name',
    label: 'Channel P. 2',
    options: {
      sort: true,
      display: true
    }
  }
];

export const ORDER_BY_OPTIONS = {
  column: 'recruiterName',
  direction: 'asc'
};
